import {
    useState,
    useEffect,
    useCallback,
    KeyboardEventHandler,
    ChangeEventHandler,
} from 'react';
import { useStore } from '@ui/hooks/useStore';
import useKeyDown from '@ui/hooks/useKeyDown';
import { ICloseEvent } from '@core/interfaces/ICloseEvent';
import { IState } from './IState';

const useTechSupport = (onClose: ICloseEvent['onClose']) => {
    const { lang, support } = useStore();
    const key = useKeyDown();

    const initState: IState = {
        topic: '',
        topicError: false,
        content: '',
        contentError: false,
    };
    const [state, setState] = useState(() => initState);

    const handleInputClick = useCallback(() => {
        support.resetServerError();
        setState((prev) => ({
            ...prev,
            topicError: false,
            contentError: false,
        }));
    }, []);

    const handleTopicChange: ChangeEventHandler<HTMLInputElement> = useCallback(
        (e) => {
            const { value: topic } = e.target as HTMLInputElement;
            setState((prev) => ({ ...prev, topic }));
        },
        [handleInputClick],
    );

    const handleContentChange: ChangeEventHandler<HTMLTextAreaElement> =
        useCallback(
            (e) => {
                const { value: content } = e.target as HTMLTextAreaElement;
                setState((prev) => ({ ...prev, content }));
            },
            [handleInputClick],
        );

    const handleSendClick = useCallback(() => {
        let isValid = true;

        if (!state.content) {
            isValid = false;
            setState((prev) => ({ ...prev, contentError: true }));
        }

        if (!state.topic) {
            isValid = false;
            setState((prev) => ({ ...prev, topicError: true }));
        }

        if (!isValid) return;

        support.createReport(state.topic, state.content);
    }, [state.content, state.topic]);

    const handleKey = useCallback(
        (key: string) => {
            switch (key) {
                case 'Escape':
                    onClose();
                    break;
            }
        },
        [handleSendClick, onClose],
    );

    const handleKeyDown: KeyboardEventHandler<HTMLElement> = useCallback(
        (e) => {
            const { key } = e;
            handleInputClick();
            if (key === 'Enter') {
                e.stopPropagation();
                e.preventDefault();
                handleKey(key);
            }
        },
        [handleKey, handleInputClick],
    );

    const handleKeyDownTextArea: KeyboardEventHandler<HTMLTextAreaElement> =
        useCallback(
            (e) => {
                const { key } = e;
                handleInputClick();
                if (key === 'Enter') {
                    e.stopPropagation();
                    e.preventDefault();
                }
            },
            [handleInputClick],
        );

    const handleBtnKeyDown: KeyboardEventHandler<HTMLButtonElement> =
        useCallback(
            (e) => {
                const { key } = e;
                handleInputClick();
                if (key === 'Enter') {
                    e.stopPropagation();
                    e.preventDefault();
                    const target = e.target as HTMLElement;
                    target.click();
                }
            },
            [handleInputClick],
        );

    const handlers = {
        handleTopicChange,
        handleContentChange,
        handleSendClick,
        handleKeyDown,
        handleKeyDownTextArea,
        handleBtnKeyDown,
        handleInputClick,
    };

    useEffect(() => {
        handleInputClick();
        return () => {
            handleInputClick();
            if (support.operationSuccess) {
                support.resetOperationSuccess();
            }
        };
    }, []);

    useEffect(() => {
        if (support.operationSuccess) {
            onClose();
        }
    }, [support.operationSuccess]);

    return { lang, support, state, handlers };
};

export default useTechSupport;
